// Decline workflow stage 2 — "Confirm Decline" (final ratification)
// modal. LA + Admin only. Mirrors the CertifyForm / DealOutcome Finalize
// attestation pattern: three required checkboxes, an optional audit-visible
// notes field. The Confirm button stays disabled until all three
// attestations are checked.
//
// Note: the optional decline-letter Document Repository link is deferred —
// the attestation alone is the source of truth. The backend FK +
// banner link are in place; a deal-documents picker is not built yet.

import { useEffect, useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useConfirmDecline } from "@/hooks/deals/useDeclineWorkflow";
import type { DealDeclineStateDto } from "@/types/deals";

interface Props {
  open: boolean;
  dealId: string;
  dealNumber: string;
  proposal: DealDeclineStateDto | null;
  onClose: () => void;
}

// The third attestation and the warning above them state what ratifying does
// (owner ruling, 2026-10-26): the deal is locked by DeclinedDealReadOnlyMiddleware
// and nothing reactivates it. They used to promise an admin reactivation through
// Admin Undo, a later phase of the decline workflow that the retired Deal Flow
// spec deferred (AdminUndoService.UndoDeclineRatificationAsync;
// #230), which was never built; Deal Pipeline spec §6.6 now says a declined
// deal may not be reactivated and is cloned instead. DealDeclineService names the
// third attestation in its refusal with the same words.
const ATTESTATIONS = [
  "I confirm the formal decline letter has been printed and sent to the parties.",
  "I confirm the decline reason is documented in the deal record.",
  "I confirm this decline is final and irreversible.",
] as const;

export function DeclineConfirmModal({
  open,
  dealId,
  dealNumber,
  proposal,
  onClose,
}: Props) {
  const toast = useToast();
  const confirm = useConfirmDecline(dealId);
  const [att, setAtt] = useState<[boolean, boolean, boolean]>([false, false, false]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) {
      setAtt([false, false, false]);
      setNotes("");
    }
  }, [open]);

  const allAttested = att[0] && att[1] && att[2];

  function toggle(i: 0 | 1 | 2) {
    setAtt((prev) => {
      const next: [boolean, boolean, boolean] = [prev[0], prev[1], prev[2]];
      next[i] = !prev[i];
      return next;
    });
  }

  async function submit() {
    if (!allAttested || confirm.isPending) return;
    try {
      await confirm.mutateAsync({
        letterPrintedAndSentAttested: att[0],
        reasonDocumentedAttested: att[1],
        finalIrreversibleAttested: att[2],
        notes: notes.trim() ? notes.trim() : null,
        linkedDeclineLetterDocumentId: null,
      });
      toast.success("Decline ratified. The deal is now locked from edits.");
      onClose();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      open={open}
      onClose={confirm.isPending ? () => {} : onClose}
      dismissOnBackdropClick={false}
      title="Confirm Decline (Final Ratification)?"
      description={dealNumber}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={confirm.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={!allAttested || confirm.isPending}
            loading={confirm.isPending}
          >
            CONFIRM: RATIFY DECLINE
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <p className="font-semibold text-status-error" role="alert">
          ⚠ This is the final decline ratification. Once confirmed, the deal
          is locked from further edits and cannot be reactivated. It can still
          be cloned, and only an admin can archive or restore it.
        </p>

        {proposal?.proposedReason && (
          <div className="text-small text-gray-dark">
            <p className="font-semibold">Original proposal:</p>
            <blockquote className="italic border-l-2 border-border-light pl-space-2 mt-space-1">
              "{proposal.proposedReason}"
              {proposal.proposedByDisplayName
                ? ` — ${proposal.proposedByDisplayName}`
                : ""}
            </blockquote>
          </div>
        )}

        <p className="mt-space-2 text-small font-semibold text-onyx">
          Required attestations (all three):
        </p>
        <div className="grid gap-space-2">
          {ATTESTATIONS.map((label, i) => (
            <label
              key={i}
              className="flex items-start gap-space-2 text-small text-onyx"
            >
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                checked={att[i]}
                onChange={() => toggle(i as 0 | 1 | 2)}
                disabled={confirm.isPending}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>

        <label
          htmlFor="decline-confirm-notes"
          className="block mt-space-2 text-small font-display uppercase tracking-label text-gold-shadow"
        >
          Decline notes (optional, audit-visible)
        </label>
        <textarea
          id="decline-confirm-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full border border-border-light rounded-md p-space-2 text-small focus:outline-none focus-visible:shadow-goldRing"
          rows={3}
          disabled={confirm.isPending}
        />
      </div>
    </Modal>
  );
}
